/**
 * Attribute Extraction (EATTEXT) — AutoCAD Express Tools parity.
 *
 * Walks every BlockInsertEntity in the drawing, gathers its attribute
 * values (ATTRIB children and Attdef defaults), and produces a CSV
 * representation ready for download. Mirrors the AutoCAD 2024+ `EATTEXT`
 * command: user-selected block / attribute filters, include-invisible
 * and include-constant toggles, one row per INSERT, columns aligned so
 * missing tags are empty cells.
 *
 * The function is pure — the DrawingDocument argument is never mutated.
 *
 * No reducer case: EATTEXT is a side-effect (browser download). The UI
 * runs the mutation, feeds the result into an `<a download>` element,
 * appends a command-feed summary.
 *
 * ## Dependency map
 *
 * extractAttributes(input)
 *   ├── normaliseFilters(input)                    — coerce optional sets
 *   ├── collectAttributesForInsert(insert, defs)   — merge ATTRIB + Attdef
 *   │     (defaults are used when the INSERT has no matching ATTRIB,
 *   │      mirroring AutoCAD Express Tools behaviour)
 *   ├── computeColumnOrder(rows)                   — stable union of tags
 *   └── serialiseCsv(rows, columnOrder)            — RFC-4180 compliant
 */

import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";

// ═══════════════════════════════════════════════════════════════════
// Public types
// ═══════════════════════════════════════════════════════════════════

export interface AttributeExtractionInput {
  readonly drawingDocument: DrawingDocument;
  /**
   * Optional block-name whitelist. `undefined` / empty array → all blocks.
   * Reserved (anonymous) blocks like `*Model_Space` are always skipped
   * regardless of the filter.
   */
  readonly blockFilter?: readonly string[];
  /**
   * If `false` (default), ATTDEF entries whose Invisible flag is set and
   * INSERT attribute children whose invisible flag is set are omitted.
   */
  readonly includeInvisible?: boolean;
  /**
   * If `false` (default), ATTDEF entries whose Constant flag is set are
   * omitted from the output. Constant attributes have no corresponding
   * ATTRIB child in the INSERT — their value always equals the ATTDEF
   * default — so AutoCAD hides them from EATTEXT by default.
   */
  readonly includeConstant?: boolean;
  /**
   * Optional tag whitelist. `undefined` / empty array → all tags.
   */
  readonly attributesFilter?: readonly string[];
}

export interface AttributeExtractionRow {
  readonly insertId: string;
  readonly blockName: string;
  readonly spaceId: string;
  /** Map of tag → value (includes only tags the row exposes). */
  readonly attributes: Record<string, string>;
  readonly insertionX: number;
  readonly insertionY: number;
}

export interface AttributeExtractionResult {
  readonly rows: readonly AttributeExtractionRow[];
  readonly columnOrder: readonly string[];
  readonly csv: string;
  readonly summary: string;
}

// ═══════════════════════════════════════════════════════════════════
// Public entry point
// ═══════════════════════════════════════════════════════════════════

export function extractAttributes(
  input: AttributeExtractionInput,
): AttributeExtractionResult {
  const {
    drawingDocument,
    blockFilter,
    attributesFilter,
    includeInvisible = false,
    includeConstant = false,
  } = input;

  const blockSet =
    blockFilter && blockFilter.length > 0
      ? new Set(blockFilter)
      : undefined;
  const tagSet =
    attributesFilter && attributesFilter.length > 0
      ? new Set(attributesFilter)
      : undefined;

  const rows: AttributeExtractionRow[] = [];

  for (const space of drawingDocument.spaces) {
    for (const entity of space.entities) {
      if (entity.type !== "Block") continue;
      if (blockSet && !blockSet.has(entity.blockName)) continue;

      const blockDef = drawingDocument.blocks?.[entity.blockName];
      if (!blockDef) continue; // Missing blocks produce no rows.
      if (isReservedBlockName(blockDef.name)) continue;

      // Build the tag → value map for this insert.
      const attrs: Record<string, string> = {};

      // 1. ATTDEFs inside the block definition → these define the row's
      //    column names and supply default values when the INSERT lacks
      //    a matching ATTRIB.
      for (const def of blockDef.entities) {
        if (def.type !== "Attdef") continue;
        const tag = def.tag ?? "";
        if (!tag) continue;
        if (tagSet && !tagSet.has(tag)) continue;
        const isInvisible = def.flags?.invisible === true;
        const isConstant = def.flags?.constant === true;
        if (isInvisible && !includeInvisible) continue;
        if (isConstant && !includeConstant) continue;
        attrs[tag] = def.defaultValue ?? "";
      }

      // 2. INSERT's ATTRIB children override ATTDEF defaults.
      const instanceAttributes = entity.attributes ?? [];
      for (const att of instanceAttributes) {
        const tag = att.tag ?? "";
        if (!tag) continue;
        if (tagSet && !tagSet.has(tag)) continue;
        if (att.invisible === true && !includeInvisible) continue;
        // Constant ATTRIBs never appear as children — they live on the
        // ATTDEF. Still honour the filter defensively.
        attrs[tag] = att.value ?? "";
      }

      // If the row has no tags (e.g. every ATTDEF was filtered out),
      // still emit a row so users see the INSERT in the output.
      rows.push({
        insertId: entity.id,
        blockName: entity.blockName,
        spaceId: space.id,
        attributes: attrs,
        insertionX: entity.insertionPoint.x,
        insertionY: entity.insertionPoint.y,
      });
    }
  }

  const columnOrder = computeColumnOrder(rows);
  const csv = serialiseCsv(rows, columnOrder);
  const summary = `Extracted ${rows.length} INSERT row${rows.length === 1 ? "" : "s"} across ${columnOrder.length} attribute column${columnOrder.length === 1 ? "" : "s"}.`;

  return { rows, columnOrder, csv, summary };
}

// ═══════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════

/**
 * Anonymous blocks (`*Model_Space`, `*Paper_Space*`, `*D\d+`, `*U\d+`,
 * `*X\d+`) are AutoCAD-managed and are never meaningful in EATTEXT
 * output. We skip them regardless of user filters.
 */
function isReservedBlockName(name: string): boolean {
  if (name === "*Model_Space") return true;
  if (name.startsWith("*Paper_Space")) return true;
  // Anonymous blocks: *D… / *U… / *X…
  if (/^\*[DUX]\d+$/.test(name)) return true;
  return false;
}

/**
 * Determine the stable column order: header + every tag that appears
 * in at least one row, in first-seen order. Keeps AutoCAD-compatible
 * column ordering where the first INSERT's ATTDEF declaration order
 * wins; subsequent blocks' tags append in their first-seen order.
 */
function computeColumnOrder(
  rows: readonly AttributeExtractionRow[],
): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const row of rows) {
    for (const tag of Object.keys(row.attributes)) {
      if (seen.has(tag)) continue;
      seen.add(tag);
      order.push(tag);
    }
  }
  return order;
}

/**
 * Serialise rows to RFC-4180 CSV. Every field is double-quoted so the
 * output survives tags that contain commas, newlines, or quotes. The
 * header row is: `Block,InsertID,Space,X,Y,<tag1>,<tag2>,...`.
 */
function serialiseCsv(
  rows: readonly AttributeExtractionRow[],
  columnOrder: readonly string[],
): string {
  const lines: string[] = [];
  const header = [
    "Block",
    "InsertID",
    "Space",
    "X",
    "Y",
    ...columnOrder,
  ].map(escapeCsvField);
  lines.push(header.join(","));
  for (const row of rows) {
    const fields: string[] = [
      row.blockName,
      row.insertId,
      row.spaceId,
      formatNumber(row.insertionX),
      formatNumber(row.insertionY),
      ...columnOrder.map((tag) => row.attributes[tag] ?? ""),
    ].map(escapeCsvField);
    lines.push(fields.join(","));
  }
  // CRLF line endings — AutoCAD / Excel both prefer \r\n in CSV.
  return lines.join("\r\n") + "\r\n";
}

function escapeCsvField(value: string): string {
  // RFC-4180: always double-quote; escape inner quotes by doubling.
  const escaped = value.replace(/"/g, '""');
  return `"${escaped}"`;
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return "0";
  // Match AutoCAD formatting: integer when round, else up to 6 decimal
  // places, no trailing zeros.
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return parseFloat(n.toFixed(6)).toString();
}
